import { useCallback, useMemo, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Alert, Button, Stack } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { formatNumber } from "@/features/shared";
import { ExpenseDialog, useSnackbar } from "@/features/ui";
import { ReturnableTypeEnum } from "../../returnable-type";
import { supportsExpenses } from "../../sales-return-rules";
import { SelectSalesInvoiceExpenseDialog } from "./select-sales-invoice-expense-dialog";

/**
 * Expenses charged back by a return. What may be returned depends entirely on
 * the source document:
 * - SALES_INVOICE: only the source invoice's own expense lines, each capped at
 *   its remaining returnable amount. Picked, never typed.
 * - NON_INVOICE: free-form — any expense account, any amount.
 * - DELIVERY_ORDER: not supported by the backend at all.
 */
export const ExpensesSection = () => {
  const { control, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);

  const { append, remove, update } = useFieldArray({
    control,
    name: "salesReturnExpenses",
  });

  const [
    salesReturnExpenses,
    returnableType,
    returnableReferenceNumber,
    seller,
  ] = useWatch({
    control,
    name: [
      "salesReturnExpenses",
      "returnableType",
      "returnableReferenceNumber",
      "seller",
    ],
  });

  // DataGrid freezes whatever array it gets as `rows` (a dev-only guard against
  // rows.push()). useWatch hands back react-hook-form's own array, and freezing
  // that breaks every later write: RHF's set() self-assigns each path segment,
  // so even `salesReturnExpenses.0.amount` has to write index 0. Give the grid
  // a copy and leave the form's array alone.
  const rows = useMemo(
    () => [...salesReturnExpenses],
    [salesReturnExpenses]
  );

  const isInvoiceReturn = returnableType === ReturnableTypeEnum.SALES_INVOICE;
  const isDeliveryOrderReturn = !supportsExpenses(returnableType);

  const handleDelete = useCallback(
    (id) => () => {
      const index = salesReturnExpenses.findIndex(
        (expense) => expense.id === id
      );

      if (index !== -1) {
        remove(index);
        showSnackbar("Biaya berhasil dihapus", "success");
      }
    },
    [salesReturnExpenses, remove, showSnackbar]
  );

  const handleAttachInvoiceExpenses = (selectedExpenses) => {
    selectedExpenses.forEach(
      ({
        id: salesInvoiceExpenseId,
        outletChartOfAccount,
        remainingAmount,
        note,
      }) => {
        const alreadyExists = salesReturnExpenses.some(
          (expense) => expense.salesInvoiceExpenseId === salesInvoiceExpenseId
        );

        if (!alreadyExists) {
          append({
            id: ulid(),
            salesInvoiceExpenseId,
            outletChartOfAccount,
            remainingAmount,
            amount: remainingAmount,
            note: note || null,
          });
        }
      }
    );
  };

  const handleSaveFreeFormExpense = (data) => {
    const expense = {
      id: salesReturnExpenses[editingIndex]?.id ?? ulid(),
      salesInvoiceExpenseId: null,
      outletChartOfAccount: data.outletChartOfAccount,
      remainingAmount: null,
      amount: data.amount,
      note: data.note || null,
    };

    if (editingIndex !== null) {
      update(editingIndex, expense);
    } else {
      append(expense);
    }
  };

  const processRowUpdate = (newRow) => {
    return new Promise((resolve, reject) => {
      const amount = Number(newRow.amount);

      if (Number.isNaN(amount) || amount < 0) {
        reject(new Error("Nominal tidak boleh kurang dari 0"));
        return;
      }

      // Invoice-sourced lines are capped by the backend at the source line's
      // remaining amount; free-form lines have no ceiling.
      if (newRow.remainingAmount !== null && amount > newRow.remainingAmount) {
        reject(
          new Error(
            `Nominal tidak boleh melebihi sisa retur (${formatNumber(
              newRow.remainingAmount
            )})`
          )
        );
        return;
      }

      const index = salesReturnExpenses.findIndex(
        (expense) => expense.id === newRow.id
      );

      if (index === -1) {
        reject(new Error("Biaya tidak ditemukan"));
        return;
      }

      setValue(`salesReturnExpenses.${index}.amount`, amount, {
        shouldDirty: true,
      });
      resolve({ ...newRow, amount });
    });
  };

  const columns = [
    {
      field: "outletChartOfAccount",
      headerName: "Akun",
      flex: 3,
      valueGetter: (value) => (value ? `${value.code} - ${value.name}` : "-"),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      valueGetter: (value) => value || "-",
    },
    ...(isInvoiceReturn
      ? [
          {
            field: "remainingAmount",
            headerName: "Sisa Retur",
            type: "number",
            flex: 1,
            valueFormatter: (value) => formatNumber(value),
          },
        ]
      : []),
    {
      field: "amount",
      headerName: "Retur",
      type: "number",
      flex: 1,
      editable: true,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      getActions: ({ id, row }) => [
        // Free-form lines carry their own account, so they stay fully editable.
        ...(row.salesInvoiceExpenseId
          ? []
          : [
              <GridActionsCellItem
                key={`edit-${id}`}
                icon={<EditIcon />}
                label="Ubah"
                onClick={() => {
                  setEditingIndex(
                    salesReturnExpenses.findIndex(
                      (expense) => expense.id === id
                    )
                  );
                  setExpenseDialogOpen(true);
                }}
                color="primary"
              />,
            ]),
        <GridActionsCellItem
          key={`delete-${id}`}
          icon={<DeleteIcon />}
          label="Hapus"
          onClick={handleDelete(id)}
          color="error"
        />,
      ],
    },
  ];

  if (isDeliveryOrderReturn) {
    return (
      <Alert severity="info">
        Retur dari Surat Jalan tidak dapat menyertakan biaya. Gunakan Faktur
        Penjualan atau Non-Faktur bila biaya perlu diretur.
      </Alert>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        {isInvoiceReturn ? (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!returnableReferenceNumber}
            onClick={() => setSelectDialogOpen(true)}
          >
            Ambil Biaya Faktur
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingIndex(null);
              setExpenseDialogOpen(true);
            }}
          >
            Tambah Biaya
          </Button>
        )}
      </Stack>

      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={columns}
        rows={rows}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          showSnackbar(error.message, "error")
        }
        disableRowSelectionOnClick
        hideFooter
      />

      {selectDialogOpen && (
        <SelectSalesInvoiceExpenseDialog
          outletId={seller?.id}
          referenceNumber={returnableReferenceNumber}
          attachedExpenses={salesReturnExpenses}
          onClose={() => setSelectDialogOpen(false)}
          onSubmit={handleAttachInvoiceExpenses}
        />
      )}

      {expenseDialogOpen && (
        <ExpenseDialog
          outletId={seller?.id}
          title={editingIndex !== null ? "Ubah Biaya" : "Tambah Biaya"}
          defaultValues={
            editingIndex !== null
              ? {
                  outletChartOfAccount:
                    salesReturnExpenses[editingIndex].outletChartOfAccount,
                  amount: salesReturnExpenses[editingIndex].amount,
                  note: salesReturnExpenses[editingIndex].note ?? "",
                }
              : undefined
          }
          onClose={() => {
            setExpenseDialogOpen(false);
            setEditingIndex(null);
          }}
          onSubmit={handleSaveFreeFormExpense}
        />
      )}
    </Stack>
  );
};
